<template>
    <label
        :for="id"
        class="tw-text-xs tw-text-gray-600 tw-select-none tw-cursor-pointer tw-font-switzer tw-flex tw-relative tw-items-center tw-gap-[6px] tw-mb-1"
        :class="[
            {
                'tw-cursor-default': disabled,
            },
            attrs.class,
        ]"
    >
        <input
            :id
            ref="input"
            v-model="model"
            :readonly
            v-bind="inputAttrs"
            type="checkbox"
            :disabled
            :value
            @change="(e) => emit('change', e)"
        />
        <slot />
    </label>
</template>

<script setup lang="ts">
import { computed, watch, useAttrs, useTemplateRef } from 'vue';
import { excludeKeys } from '@/js/utils.js';

defineOptions({
    inheritAttrs: false,
});

const attrs = useAttrs();
const emit = defineEmits(['change']);
const inputAttrs = computed(() => excludeKeys(attrs, ['class']));

const { indeterminate } = defineProps({
    id: {
        type: String,
        required: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    indeterminate: {
        type: Boolean,
        default: false,
    },
    value: {
        type: [String, Number],
        required: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
});

const input = useTemplateRef('input');
const model = defineModel<boolean | Array<unknown>>({
    type: [Boolean, Array],
    default: false,
});

watch(
    () => indeterminate,
    (value) => {
        if (!input.value) return;
        input.value.indeterminate = value;
    },
    {
        immediate: true,
    }
);
</script>

<style lang="scss" scoped>
[type='checkbox'] {
    @apply tw-bg-white tw-border tw-rounded tw-shadow-sm tw-text-eventwise-500 tw-size-4 tw-appearance-none;
    &:checked {
        @apply tw-bg-eventwise-400 tw-border-eventwise;
        background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e");
    }
    &:not(disabled) {
        @apply tw-cursor-pointer;
    }
    &:disabled {
        @apply tw-bg-gray-200 tw-border-gray-200 tw-text-gray-400 tw-cursor-not-allowed;
    }
    &:indeterminate {
        background-image: url('@/images/dash.svg');
    }
}
</style>
